import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Share2 } from 'lucide-react';
import { AutoScrollImages } from '../AutoScrollImages';
import { RatingBadge } from '../RatingBadge';

const FALLBACK = 'https://images.unsplash.com/photo-1561501900-3701fa6a0864?w=800';

/**
 * Large detailed hotel card used in HomePage search-results section.
 */
export const HotelListCard = ({ hotel, onShare, onOpenGallery, detailsUrl }) => {
  const navigate = useNavigate();
  const href = detailsUrl || `/hotels/${hotel._id}`;
  const minPrice = hotel.min_price || 0;
  const imgs = hotel.images || [];
  const hasImgs = imgs.length > 0;
  const showImgs = hasImgs ? imgs : [FALLBACK];

  return (
    <div
      className="bg-white rounded-2xl border border-gray-200 hover:shadow-lg transition-all overflow-hidden"
      data-testid={`hotel-card-${hotel._id}`}
    >
      <div className="flex flex-col md:flex-row">
        <div
          className="md:w-80 h-64 md:h-auto bg-gray-100 relative cursor-pointer group overflow-hidden"
          onClick={() => (hasImgs ? onOpenGallery(imgs, hotel.hotel_name) : navigate(href))}
        >
          <AutoScrollImages
            images={showImgs}
            alt={hotel.hotel_name}
            interval={3000}
            className="group-hover:scale-105 transition-transform duration-500"
          />
          {imgs.length > 1 && (
            <div className="absolute bottom-3 right-3 bg-white/95 backdrop-blur text-black px-2.5 py-1 rounded-md text-xs font-bold z-10">
              +{imgs.length} Photos
            </div>
          )}
        </div>
        <div className="flex-1 p-5 md:p-7 cursor-pointer" onClick={() => navigate(href)}>
          <div className="flex items-start justify-between gap-4 mb-2">
            <div className="flex-1">
              <h3 className="text-xl font-black text-black hover:text-[#FF0059] transition-colors line-clamp-1">{hotel.hotel_name}</h3>
              <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                <MapPin className="h-3.5 w-3.5 text-[#FF0059]" /> {hotel.hotel_address}
              </p>
            </div>
            <div className="flex items-start gap-2">
              <button
                onClick={(e) => onShare(e, hotel)}
                className="p-2 rounded-full border border-gray-200 hover:border-[#FF0059] hover:bg-[#FFE5EE] transition-colors"
                aria-label="Share hotel"
                data-testid={`share-hotel-${hotel._id}`}
              >
                <Share2 className="h-4 w-4 text-gray-700" />
              </button>
              {hotel.rating > 0 && (
                <div className="text-right">
                  <RatingBadge value={hotel.rating} count={hotel.rating_count} size="md" testId={`list-rating-${hotel._id}`} />
                </div>
              )}
            </div>
          </div>
          {hotel.description && <p className="text-sm text-gray-600 line-clamp-2 mt-2">{hotel.description}</p>}
          {Array.isArray(hotel.badges) && hotel.badges.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`hotel-badges-${hotel._id}`}>
              {hotel.badges.slice(0, 4).map((b, i) => (
                <span
                  key={b}
                  className="px-2.5 py-1 text-[11px] font-black uppercase tracking-wider rounded-full"
                  style={{ background: i % 2 === 0 ? '#FF0059' : '#FFCB00', color: i % 2 === 0 ? '#fff' : '#000' }}
                >
                  {b}
                </span>
              ))}
            </div>
          )}
          {hotel.amenities && hotel.amenities.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {hotel.amenities.slice(0, 5).map((a) => (
                <span key={a} className="px-2.5 py-1 text-xs font-semibold text-[#FF0059] bg-[#FFE5EE] rounded-full">
                  {a}
                </span>
              ))}
              {hotel.amenities.length > 5 && (
                <span className="px-2.5 py-1 text-xs font-semibold text-gray-500 bg-gray-100 rounded-full">
                  +{hotel.amenities.length - 5} more
                </span>
              )}
            </div>
          )}
          <div className="flex items-end justify-between mt-5 pt-4 border-t border-gray-100">
            <div className="flex gap-1.5">
              {hotel.hotel_category && (
                <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-gray-600 bg-gray-100 rounded">
                  {hotel.hotel_category}
                </span>
              )}
            </div>
            <div className="text-right">
              <p className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">Starting from</p>
              <p className="text-2xl font-black text-black">₹{minPrice.toLocaleString('en-IN')}</p>
              <p className="text-[10px] text-gray-500">per night · pay at hotel</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
